/**
 * Shared API client configuration and utilities
 */

import { getErrorMessage } from "@/services/utils";

export const BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const FETCH_DEFAULT_OPTIONS: RequestInit = {
  headers: {
    "Content-Type": "application/json",
    "X-SKH-Content-Type": "front",
  },
  credentials: "include",
};

export function checkResponseStatus(r: Response, expectedStatusCode: number) {
  if (r.status !== expectedStatusCode) {
    throw new Error(`Server responded with unexpected status code: ${r.status}`);
  }
}

/**
 * Options for API request methods
 */
export interface ApiRequestOptions<T> {
  /** Expected HTTP status code (default: 200) */
  expectedStatus?: number;
  /** Default value to return on error */
  defaultValue?: T;
  /** Custom error handler (if not provided, logs to console) */
  onError?: (error: unknown, statusCode?: number) => void;
  /** Whether to parse JSON response (default: true) */
  parseJson?: boolean;
  /** AbortSignal to cancel the request (e.g. from AbortController) */
  signal?: AbortSignal;
}

/**
 * Generic API request handler
 */
type ApiRequestReturn<T, D> = D extends undefined ? T | undefined : T | D;

async function apiRequest<T, D = undefined>(
  url: string,
  method: string,
  options: ApiRequestOptions<T> & { defaultValue?: D } = {},
  body?: unknown
): Promise<ApiRequestReturn<T, D>> {
  const { expectedStatus = 200, defaultValue, onError, parseJson = true } = options;

  let response: Response | undefined;

  try {
    const requestOptions: RequestInit = {
      method,
      ...FETCH_DEFAULT_OPTIONS,
      ...(options.signal && { signal: options.signal }),
    };

    if (body !== undefined) {
      requestOptions.body = typeof body === "string" ? body : JSON.stringify(body);
    }

    response = await fetch(`${BASE_URL}${url}`, requestOptions);
    checkResponseStatus(response, expectedStatus);

    if (parseJson && expectedStatus !== 204) {
      return (await response.json()) as ApiRequestReturn<T, D>;
    }

    return defaultValue as ApiRequestReturn<T, D>;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw error;
    }
    if (onError) {
      onError(error, response?.status);
    } else {
      console.error(getErrorMessage(error));
    }
    return defaultValue as ApiRequestReturn<T, D>;
  }
}

/**
 * HTTP GET request
 *
 * Overloads:
 * - Without defaultValue: Returns Promise<T | undefined> (may return undefined on error)
 * - With defaultValue: Returns Promise<T> (always returns a value, never undefined)
 */
export async function apiGet<T>(
  url: string,
  options?: ApiRequestOptions<T> & { defaultValue?: undefined }
): Promise<T | undefined>;
export async function apiGet<T>(
  url: string,
  options: ApiRequestOptions<T> & { defaultValue: T }
): Promise<T>;
export async function apiGet<T>(
  url: string,
  options?: ApiRequestOptions<T>
): Promise<T | undefined> {
  if (options?.defaultValue !== undefined) {
    // When defaultValue is provided, it must be of type T, so D = T
    return apiRequest<T, T>(url, "GET", options as ApiRequestOptions<T> & { defaultValue: T });
  }
  return apiRequest<T, undefined>(
    url,
    "GET",
    options as ApiRequestOptions<T> & { defaultValue?: undefined }
  );
}

/**
 * HTTP POST request
 *
 * Overloads:
 * - Without defaultValue: Returns Promise<T | undefined> (may return undefined on error)
 * - With defaultValue: Returns Promise<T> (always returns a value, never undefined)
 */
export async function apiPost<T>(
  url: string,
  body?: unknown,
  options?: ApiRequestOptions<T> & { defaultValue?: undefined }
): Promise<T | undefined>;
export async function apiPost<T>(
  url: string,
  body: unknown | undefined,
  options: ApiRequestOptions<T> & { defaultValue: T }
): Promise<T>;
export async function apiPost<T>(
  url: string,
  body?: unknown,
  options?: ApiRequestOptions<T>
): Promise<T | undefined> {
  if (options?.defaultValue !== undefined) {
    return apiRequest<T, T>(
      url,
      "POST",
      { expectedStatus: 201, ...options } as ApiRequestOptions<T> & { defaultValue: T },
      body
    );
  }
  return apiRequest<T, undefined>(
    url,
    "POST",
    { expectedStatus: 201, ...options } as ApiRequestOptions<T> & { defaultValue?: undefined },
    body
  );
}

/**
 * HTTP PUT request
 *
 * Overloads:
 * - Without defaultValue: Returns Promise<T | undefined> (may return undefined on error)
 * - With defaultValue: Returns Promise<T> (always returns a value, never undefined)
 */
export async function apiPut<T>(
  url: string,
  body?: unknown,
  options?: ApiRequestOptions<T> & { defaultValue?: undefined }
): Promise<T | undefined>;
export async function apiPut<T>(
  url: string,
  body: unknown | undefined,
  options: ApiRequestOptions<T> & { defaultValue: T }
): Promise<T>;
export async function apiPut<T>(
  url: string,
  body?: unknown,
  options?: ApiRequestOptions<T>
): Promise<T | undefined> {
  if (options?.defaultValue !== undefined) {
    return apiRequest<T, T>(
      url,
      "PUT",
      options as ApiRequestOptions<T> & { defaultValue: T },
      body
    );
  }
  return apiRequest<T, undefined>(
    url,
    "PUT",
    options as ApiRequestOptions<T> & { defaultValue?: undefined },
    body
  );
}

/**
 * HTTP DELETE request
 *
 * Overloads:
 * - Without defaultValue: Returns Promise<T | undefined> (may return undefined on error)
 * - With defaultValue: Returns Promise<T> (always returns a value, never undefined)
 */
export async function apiDelete<T>(
  url: string,
  options?: ApiRequestOptions<T> & { defaultValue?: undefined }
): Promise<T | undefined>;
export async function apiDelete<T>(
  url: string,
  options: ApiRequestOptions<T> & { defaultValue: T }
): Promise<T>;
export async function apiDelete<T>(
  url: string,
  options?: ApiRequestOptions<T>
): Promise<T | undefined> {
  if (options?.defaultValue !== undefined) {
    return apiRequest<T, T>(url, "DELETE", {
      expectedStatus: 204,
      ...options,
    } as ApiRequestOptions<T> & { defaultValue: T });
  }
  return apiRequest<T, undefined>(url, "DELETE", {
    expectedStatus: 204,
    ...options,
  } as ApiRequestOptions<T> & { defaultValue?: undefined });
}
